import { Quill } from "react-quill-new";

/**
 * Resizable table columns for the MOU editors.
 *
 * A column's width is stored on every cell of that column as a class,
 * ql-cw-<percent> ("ql-cw-30" = 30% of the table). A class, not a style: the
 * server's sanitizer keeps ql-* classes and drops inline styles, and mou/pdf.py
 * turns the class into width="30%" for the PDF. It is a block attributor like
 * Quill's own alignment, so it is kept through save, reload and copy-paste.
 *
 * Drag a column's right border to resize it; the column to its right gives or
 * takes the difference, so the table keeps its width.
 */

export const MIN_COL_PX = 36;
export const COL_WIDTH_VALUES = Array.from({ length: 100 }, (_, i) => String(i + 1));

const Parchment = Quill.import("parchment");
const ColWidth = new Parchment.ClassAttributor("colwidth", "ql-cw", {
  scope: Parchment.Scope.BLOCK,
  whitelist: COL_WIDTH_VALUES,
});
Quill.register({ "formats/colwidth": ColWidth }, true);

export const COL_WIDTH_CSS = `
.ql-editor table td { position: relative; }
.ql-editor table td.mou-col-hover { box-shadow: inset -2px 0 0 #6366f1; }
${COL_WIDTH_VALUES.map((v) => `.ql-editor td.ql-cw-${v} { width: ${v}%; }`).join("\n")}
.mou-col-guide { position: absolute; width: 0; border-left: 2px dashed #6366f1; pointer-events: none; z-index: 5; }
.mou-col-tip { position: absolute; z-index: 6; pointer-events: none; transform: translate(-50%, -100%);
  background: #4338ca; color: #fff; font-size: 10px; padding: 1px 6px; border-radius: 4px; white-space: nowrap; }
`;

/**
 * New pixel widths after dragging the border between column i and i+1 by
 * `delta` px. The pair's total stays the same and neither goes below `min`.
 */
export function resizePair(widths, i, delta, min = MIN_COL_PX) {
  const next = widths.slice();
  if (i < 0 || i >= widths.length - 1) return next;
  const pair = widths[i] + widths[i + 1];
  const left = Math.min(Math.max(widths[i] + delta, min), pair - min);
  next[i] = left;
  next[i + 1] = pair - left;
  return next;
}

/** Whole percentages of the total, each at least 1, summing to exactly 100. */
export function toPercents(widths) {
  const total = widths.reduce((a, b) => a + b, 0) || 1;
  const exact = widths.map((w) => (w / total) * 100);
  const out = exact.map((x) => Math.max(1, Math.floor(x)));
  let rest = 100 - out.reduce((a, b) => a + b, 0);
  // Hand out what rounding down left over to the largest remainders first;
  // take back from the widest columns if the 1% floor overshot.
  const order = exact.map((x, i) => [x - Math.floor(x), i]).sort((a, b) => b[0] - a[0]);
  for (let k = 0; rest > 0; k = (k + 1) % order.length, rest -= 1) out[order[k][1]] += 1;
  while (rest < 0) {
    const widest = out.indexOf(Math.max(...out));
    out[widest] -= 1;
    rest += 1;
  }
  return out;
}

function cellsOfRow(row) {
  return Array.from(row.children).filter((c) => c.tagName === "TD" || c.tagName === "TH");
}

/** Apply percentages to every cell of the table, column by column. */
export function applyColumnWidths(quill, table, percents) {
  for (const row of table.querySelectorAll("tr")) {
    cellsOfRow(row).forEach((cell, j) => {
      const blot = Quill.find(cell);
      if (!blot || j >= percents.length) return;
      quill.formatLine(quill.getIndex(blot), 1, "colwidth", String(percents[j]), "user");
    });
  }
}

/** Remove stored widths from the table the cursor is in. */
export function clearColumnWidths(quill) {
  const sel = quill.getSelection(true);
  if (!sel) return false;
  const [line] = quill.getLine(sel.index);
  const table = line?.domNode?.closest?.("table");
  if (!table) return false;
  for (const cell of table.querySelectorAll("td")) {
    const blot = Quill.find(cell);
    if (blot) quill.formatLine(quill.getIndex(blot), 1, "colwidth", false, "user");
  }
  return true;
}

const EDGE_PX = 6;

/**
 * Wire column dragging onto an editor. `host` is a positioned element that
 * contains the editor, where the guide line is drawn. Returns a detach().
 */
export function attachColumnResize(quill, host) {
  const root = quill.root;
  let hover = null;
  let drag = null;

  const clearHover = () => {
    if (hover) hover.classList.remove("mou-col-hover");
    hover = null;
    root.style.cursor = "";
  };

  const edgeCell = (e) => {
    const cell = e.target?.closest?.("td");
    if (!cell || !root.contains(cell)) return null;
    const row = cell.parentElement;
    const cells = cellsOfRow(row);
    const idx = cells.indexOf(cell);
    const rect = cell.getBoundingClientRect();
    if (idx < cells.length - 1 && rect.right - e.clientX <= EDGE_PX && rect.right - e.clientX >= -2) return cell;
    // Just left of the next cell's left border counts as this border too.
    if (idx > 0 && e.clientX - rect.left <= EDGE_PX / 2) return cells[idx - 1];
    return null;
  };

  const onMove = (e) => {
    if (drag || !quill.isEnabled()) return;
    const cell = edgeCell(e);
    if (cell === hover) return;
    clearHover();
    if (cell) {
      hover = cell;
      cell.classList.add("mou-col-hover");
      root.style.cursor = "col-resize";
    }
  };

  const onDown = (e) => {
    if (!hover || e.button !== 0) return;
    e.preventDefault();
    const table = hover.closest("table");
    const first = cellsOfRow(table.querySelector("tr"));
    const col = cellsOfRow(hover.parentElement).indexOf(hover);
    const widths = first.map((c) => c.getBoundingClientRect().width);
    const hostRect = host.getBoundingClientRect();
    const tableRect = table.getBoundingClientRect();
    const guide = document.createElement("div");
    guide.className = "mou-col-guide";
    guide.style.top = `${tableRect.top - hostRect.top}px`;
    guide.style.height = `${tableRect.height}px`;
    const tip = document.createElement("div");
    tip.className = "mou-col-tip";
    tip.style.top = `${tableRect.top - hostRect.top - 2}px`;
    host.appendChild(guide);
    host.appendChild(tip);
    drag = { table, col, widths, startX: e.clientX, guide, tip, tableLeft: tableRect.left - hostRect.left, next: widths };
    update(e.clientX);
    document.addEventListener("mousemove", onDrag);
    document.addEventListener("mouseup", onUp, { once: true });
  };

  const update = (x) => {
    const next = resizePair(drag.widths, drag.col, x - drag.startX);
    drag.next = next;
    const edge = drag.tableLeft + next.slice(0, drag.col + 1).reduce((a, b) => a + b, 0);
    drag.guide.style.left = `${edge}px`;
    drag.tip.style.left = `${edge}px`;
    const pct = toPercents(next);
    drag.tip.textContent = `${pct[drag.col]}% | ${pct[drag.col + 1]}%`;
  };

  const onDrag = (e) => update(e.clientX);

  const onUp = () => {
    document.removeEventListener("mousemove", onDrag);
    const { table, next, guide, tip } = drag;
    guide.remove();
    tip.remove();
    drag = null;
    clearHover();
    applyColumnWidths(quill, table, toPercents(next));
  };

  root.addEventListener("mousemove", onMove);
  root.addEventListener("mouseleave", clearHover);
  root.addEventListener("mousedown", onDown, true);
  return () => {
    root.removeEventListener("mousemove", onMove);
    root.removeEventListener("mouseleave", clearHover);
    root.removeEventListener("mousedown", onDown, true);
    document.removeEventListener("mousemove", onDrag);
    clearHover();
  };
}
